import { useState } from "react";
import { Sparkles, Loader2, X, Phone, Target, AlertTriangle, CheckCircle, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { useAIAssistant } from "@/hooks/useAIAssistant";
import { toast } from "sonner";

interface Deal {
  id: string;
  title: string;
  amount: number | null;
  ai_win_probability: number | null;
  accounts?: { name: string } | null;
  contacts?: { first_name: string; last_name: string } | null;
  pipeline_stages?: { name: string } | null;
  expected_close_date?: string | null;
}

interface DealTalkingPointsProps {
  deal: Deal;
  isOpen: boolean;
  onClose: () => void;
}

export function DealTalkingPoints({ deal, isOpen, onClose }: DealTalkingPointsProps) {
  const [talkingPoints, setTalkingPoints] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const { chat } = useAIAssistant();

  const generateTalkingPoints = async () => {
    setIsGenerating(true);
    
    const prompt = `Generate talking points for a sales call about this deal:
- Deal: ${deal.title}
- Value: $${(deal.amount || 0).toLocaleString()}
- Stage: ${deal.pipeline_stages?.name || "Unknown"}
- Account: ${deal.accounts?.name || "Unknown"}
- Contact: ${deal.contacts ? `${deal.contacts.first_name} ${deal.contacts.last_name}` : "Unknown"}
- Win Probability: ${deal.ai_win_probability || 0}%
- Expected Close: ${deal.expected_close_date || "Not set"}

Provide:
1. Opening statement (personalized)
2. 3 key value propositions to emphasize
3. Potential objections and responses
4. Questions to ask the prospect
5. Closing strategy based on the stage

Be concise and actionable.`;

    const response = await chat(prompt);
    if (response) {
      setTalkingPoints(response);
    }
    setIsGenerating(false);
  };

  const handleCopy = () => {
    if (talkingPoints) {
      navigator.clipboard.writeText(talkingPoints);
      setCopied(true);
      toast.success("Talking points copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const probability = deal.ai_win_probability || 0;
  const getStatusColor = () => {
    if (probability >= 70) return "text-success";
    if (probability >= 40) return "text-warning";
    return "text-destructive";
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center">
              <Phone className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <p className="font-semibold">{deal.title}</p>
              <p className="text-sm text-muted-foreground font-normal">
                {deal.accounts?.name} • ${(deal.amount || 0).toLocaleString()}
              </p>
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Deal Quick Stats */}
        <div className="grid grid-cols-3 gap-3 py-3 border-y border-border">
          <div className="text-center">
            <p className="text-xs text-muted-foreground">Stage</p>
            <p className="text-sm font-medium">{deal.pipeline_stages?.name || "Unknown"}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-muted-foreground">Win Probability</p>
            <p className={cn("text-sm font-medium", getStatusColor())}>{probability}%</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-muted-foreground">Contact</p>
            <p className="text-sm font-medium truncate">
              {deal.contacts ? `${deal.contacts.first_name} ${deal.contacts.last_name}` : "None"}
            </p>
          </div>
        </div>

        {/* Talking Points Content */}
        <div className="flex-1 overflow-hidden">
          {!talkingPoints && !isGenerating ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="h-16 w-16 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center mb-4">
                <Sparkles className="h-8 w-8 text-primary" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Generate AI Talking Points</h3>
              <p className="text-muted-foreground text-sm mb-6 max-w-sm">
                Get personalized talking points, objection handlers, and closing strategies for your call.
              </p>
              <Button onClick={generateTalkingPoints} className="gap-2">
                <Sparkles className="h-4 w-4" />
                Generate Talking Points
              </Button>
            </div>
          ) : isGenerating ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
              <p className="text-muted-foreground">Generating personalized talking points...</p>
            </div>
          ) : (
            <ScrollArea className="h-[350px] pr-4">
              <div className="prose prose-sm dark:prose-invert max-w-none">
                <div className="whitespace-pre-wrap text-sm leading-relaxed">
                  {talkingPoints}
                </div>
              </div>
            </ScrollArea>
          )}
        </div>

        {/* Actions */}
        {talkingPoints && (
          <div className="flex items-center gap-2 pt-4 border-t border-border">
            <Button variant="outline" size="sm" onClick={handleCopy} className="gap-2">
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? "Copied!" : "Copy"}
            </Button>
            <Button variant="outline" size="sm" onClick={generateTalkingPoints} disabled={isGenerating} className="gap-2">
              <Sparkles className="h-4 w-4" />
              Regenerate
            </Button>
            <div className="flex-1" />
            <Button onClick={onClose}>Close</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
